import {
  AlignmentType,
  BorderStyle,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  TextRun,
  VerticalAlign,
  WidthType,
  convertMillimetersToTwip,
} from "docx";
import { ChecklistCriteria, Section } from "types/Checklist";
import { CellTextParagraphs } from "types/WordChecklist";
import { useChecklistCellMargins } from "./useChecklistCellMargins";
import { useWordChecklistCriteriaGenerator } from "./useWordChecklistCriteriaGenerator";
import { useWordFontSize } from "./useWordFontSize";

export const useWordChecklistSectionGenerator = (
  section: Section,
  allRows: TableRow[],
  allCriteria: ChecklistCriteria[],
  isNested: boolean,
  numParagraphs: number,
  numParagraphColumns: number,
  cellTextToParagraphs: CellTextParagraphs[],
  showParagraphColumns: boolean = false,
  hideScores: boolean = false,
  includeCriteriaCheckmarks: boolean = false,
  isStudentMode: boolean = false,
  useGoogleDocsCompatibleLayout: boolean = false
) => {
  var cellMargins = useChecklistCellMargins();
  var fontSize = useWordFontSize();
  

  var cells: TableCell[] = [];

  if (isNested == true) {
    cells.push(
      new TableCell({
        children: [new Paragraph("")],
      })
    );
  }

  // ok so right now there's just one cell that is being show.  I think we need to determine the col span a little more carefully depending on if we're showin the breakdown of paragraphs

  var columnSpan = 0;

  var numParagraphsToUse =
    section.paragraphPositionsData && section.paragraphPositionsData.length > 0
      ? section.paragraphPositionsData.length
      : numParagraphColumns;

  if (section.displayParagraphColumns || showParagraphColumns) {
    columnSpan = isNested ? 1 : 2;
  } else {
    columnSpan = isNested ? 2 : 3;
  }

  cells.push(
    new TableCell({
      columnSpan: columnSpan,
      margins: cellMargins,
      shading: {
        fill: "dddddd",
        type: ShadingType.CLEAR,
        color: "000000",
      },

      children: [
        new Paragraph({
          children: [
            new TextRun({
              text: section.title,
              bold: true,
              size: fontSize,
            }),
          ],
        }),
      ],
    })
  );

  if (section.displayParagraphColumns && useGoogleDocsCompatibleLayout) {
    const paragraphLabels = Array.from(
      { length: numParagraphsToUse },
      (_, index) =>
        section.paragraphPositionsData?.[index]?.shortLabel ?? `P${index + 1}`
    );

    cells.push(
      new TableCell({
        margins: cellMargins,
        shading: {
          fill: "dddddd",
          type: ShadingType.CLEAR,
          color: "000000",
        },
        verticalAlign: VerticalAlign.CENTER,
        children: [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: paragraphLabels.join(" | "),
                bold: true,
                size: fontSize,
              }),
            ],
          }),
        ],
      })
    );
  } else if (section.displayParagraphColumns) {
    var index = 1;

    var sectionParagraphPositionsData = section.paragraphPositionsData;

    var sectionColSpan = Math.floor(numParagraphColumns / numParagraphsToUse);

    var totalColumns = 0;

    const scoreCells: TableCell[] = [];

    while (index <= numParagraphsToUse) {
      totalColumns += sectionColSpan;

      var paragraphLabel = `P${index}`;

      if (
        sectionParagraphPositionsData &&
        sectionParagraphPositionsData[index - 1]
      ) {
        paragraphLabel = sectionParagraphPositionsData[index - 1].shortLabel;
      }

      // If the current key is the last keym and the total columns isn't yet equal to numParagraphs
      // Then we need to set columnSpan to the difference between numParagraphs and totalColumns
      if (
        index == numParagraphsToUse - 1 &&
        totalColumns != numParagraphColumns
      ) {
        sectionColSpan += numParagraphsToUse - totalColumns;
      }

      scoreCells.push(
        new TableCell({
          // columnSpan:
          //   numParagraphColumns != numParagraphsToUse ? totalColumns : 1,
          columnSpan: 1,
          margins: cellMargins,
          borders: {
            bottom: {
              style: BorderStyle.NONE,
            },
            top: {
              style: BorderStyle.NONE,
            },
            left: {
              style: index == 1 ? BorderStyle.NONE : BorderStyle.INSET,
              size: 2,
            },
            right: {
              style: BorderStyle.NONE,
            },
          },
          shading: {
            fill: "dddddd",
            type: ShadingType.CLEAR,
            color: "000000",
          },
          children: [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [
                new TextRun({
                  text: paragraphLabel,
                  bold: true,
                  size: fontSize,
                }),
              ],
            }),
          ],
        })
      );
      index++;
    }

    const columnWidths = [];
    const columnWidth = 3212 / scoreCells.length;

    let scoreCellIndex = 0;

    while (scoreCellIndex < scoreCells.length) {
      columnWidths.push(columnWidth);
      scoreCellIndex++;
    }

    const scoreTable = new Table({
      layout: TableLayoutType.AUTOFIT,
      borders: {
        bottom: {
          style: BorderStyle.NONE,
        },
        top: {
          style: BorderStyle.NONE,
        },
        left: {
          style: BorderStyle.NONE,
        },
        right: {
          style: BorderStyle.NONE,
        },
        insideHorizontal: {
          style: BorderStyle.NONE,
        },
        insideVertical: {
          style: BorderStyle.NONE,
        },
      },
      columnWidths: columnWidths,
      rows: [
        new TableRow({
          // height: {
          //   rule: HeightRule.AUTO,
          //   value: `100mm`,
          // },
          children: scoreCells,
        }),
      ],

      margins: {
        top: convertMillimetersToTwip(0),
        bottom: convertMillimetersToTwip(0),
        right: convertMillimetersToTwip(0),
        left: convertMillimetersToTwip(0),
      },
      width: {
        size: 100,
        type: WidthType.PERCENTAGE,
      },
    });

    const scoreCellWrapper = new TableCell({
      margins: {
        top: convertMillimetersToTwip(0),
        bottom: convertMillimetersToTwip(0),
        right: convertMillimetersToTwip(0),
        left: convertMillimetersToTwip(0),
      },
      borders: {
        bottom: {
          style: BorderStyle.INSET,
          size: 2,
        },
        top: {
          style: BorderStyle.INSET,
          size: 2,
        },
        left: {
          style: BorderStyle.INSET,
          size: 2,
        },
        right: {
          style: BorderStyle.INSET,
          size: 2,
        },
      },
      shading: {
        fill: "dddddd",
        type: ShadingType.CLEAR,
        color: "000000",
      },
      verticalAlign: VerticalAlign.CENTER,

      children: [scoreTable],
    });

    cells.push(scoreCellWrapper);
  }

  if (showParagraphColumns && section.displayParagraphColumns != true) {
    var index = 1;

    var sectionColSpan = Math.floor(numParagraphColumns / numParagraphsToUse);

    var totalColumns = 0;

    cells.push(
      new TableCell({
        columnSpan: 1,
        margins: cellMargins,
        shading: {
          fill: "dddddd",
          type: ShadingType.CLEAR,
          color: "000000",
        },
        borders: {
          bottom: {
            style: BorderStyle.NONE,
          },
          top: {
            style: BorderStyle.NONE,
          },
          left: {
            style: BorderStyle.INSET,
            size: 2,
          },
          right: {
            style: BorderStyle.INSET,
            size: 2,
          },
        },
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: "",
                bold: true,
                size: fontSize,
              }),
            ],
          }),
        ],
      })
    );
  }

  var sectionRow = new TableRow({
    children: cells,
  });

  allRows.push(sectionRow);

  for (var criteriaInstance of section.criteriaInstances) {
    var criteriaRows = useWordChecklistCriteriaGenerator(
      criteriaInstance,
      allCriteria,
      numParagraphs,
      numParagraphColumns,
      cellTextToParagraphs,
      AlignmentType.LEFT,
      "#000000",
      hideScores,
      includeCriteriaCheckmarks,
      isStudentMode,
      useGoogleDocsCompatibleLayout
    );
    allRows.push(...criteriaRows);
  }

  if (section.sections) {
    for (var nestedSection of section.sections) {
      var copiedSection = { ...nestedSection };

      if (
        copiedSection.paragraphPositionsData == undefined &&
        section.paragraphPositionsData &&
        section.paragraphPositionsData.length > 0
      ) {
        copiedSection.paragraphPositionsData = section.paragraphPositionsData;
      }

      useWordChecklistSectionGenerator(
        copiedSection,
        allRows,
        allCriteria,
        true,
        numParagraphs,
        numParagraphColumns,
        cellTextToParagraphs,
        section.displayParagraphColumns,
        hideScores,
        includeCriteriaCheckmarks,
        isStudentMode,
        useGoogleDocsCompatibleLayout
      );
    }
  }
};
